<script>
  import { derived } from "svelte/store";
  import GanttHelper from "./GanttHelper.js";

  export let GanttStore;
  export let AdjustStamp;
  export let RowDownloadUrl;

  const {
    RowsDisplayedStart,
    RowsCached,
    DisplayedRowCount,
    GanttChartType,
    RowsAllFiltered,
    RowsAllFilteredIncludingUnfoldedOperations,
    TimelineItems,
    StyleObjects,
    CurrentStyle,
    CurrentDisplayedStartDate,
    CurrentDisplayedEndDate,
    ApplyResult,
  } = GanttStore;

  let _tmp_rowdata = null;

  $: connectionLineColor =
    $GanttChartType == GanttStore.GanttChartTypes.PeggedOrderGantt
      ? "#ff3fff"
      : "blue";

  const ApplyTransactionsToData = (item, idx) => {
    if (Transactions !== null && Transactions[item] !== undefined) {
      const itemTr = Transactions[item];
      _tmp_rowdata.Data[idx]._resStatus = itemTr.Work_Status;
      _tmp_rowdata.Data[idx]._resultStart = itemTr.Work_ResultStartTime;
      _tmp_rowdata.Data[idx]._resultEnd = itemTr.Work_ResultEndTime;
      _tmp_rowdata.Data[idx]._resultQty = itemTr.Work_ResultQty;
      _tmp_rowdata.Data[idx]._resultProgress = itemTr.Work_ResultProgress;
      _tmp_rowdata.Data[idx]._resultObtainTime = itemTr.Work_ResultObtainTime;
    }
  };

  const DownloadRowIfUnloaded = (row, alias, rowType) => {
    if (RowDownloadUrl === undefined || RowDownloadUrl === "") return;
    const rowLink = RowDownloadUrl + row + ".json";
    switch (rowType) {
      case "Order":
        _tmp_rowdata = DataFetcher.FetchOrderInfo(
          row,
          rowLink,
          handleNonBlockingFetchErrors
        );
        break;
    }

    // Update data using transactions
    _tmp_rowdata.Data.forEach((item, idx) =>
      ApplyTransactionsToData(item._operation, idx)
    );
    GanttStore.RowsCached.update((cached) => {
      cachedRows = {
        ...cached,
        [row]: { ..._tmp_rowdata },
      };
      return cachedRows;
    });

    console.log(alias + " - Downloaded");
    _tmp_rowdata = null;
  };

  const IsItemVisible = (itemStart, itemEnd) => {
    return !(
      (itemStart < startDate && itemEnd < startDate) ||
      (itemStart > endDate && itemEnd > endDate)
    );
  };

  const connections = derived(
    [
      RowsAllFilteredIncludingUnfoldedOperations,
      TimelineItems,
      RowsDisplayedStart,
      RowsCached,
      GanttChartType,
      StyleObjects,
      CurrentStyle,
      CurrentDisplayedStartDate,
      CurrentDisplayedEndDate,
      ApplyResult,
    ],
    ([
      $RowsAllFilteredIncludingUnfoldedOperations,
      $TimelineItems,
      $RowsDisplayedStart,
      $RowsCached,
      $GanttChartType,
      $StyleObjects,
      $CurrentStyle,
      $CurrentDisplayedStartDate,
      $CurrentDisplayedEndDate,
      $ApplyResult,
    ]) => {
      let ret = [];

      let currentStyleIndex = $StyleObjects.indexOf($CurrentStyle);

      const CalculateStampX = (stamp) =>
        Math.max(
          0,
          Math.min(
            100,
            100 *
              GanttHelper._gantHelper_scale(
                AdjustStamp(stamp).getTime(),
                $CurrentDisplayedEndDate.getTime(),
                $CurrentDisplayedStartDate.getTime()
              )
          )
        );

      // TO:DO: Currently only show connection lines when one item is clicked
      if ($TimelineItems.length === 1) {
        let maxDepth = 1;
        let queue = new Array();
        let visited = new Set();
        queue.push([$TimelineItems[0][0], $TimelineItems[0][1], 1]);

        // BFS
        while (queue.length > 0) {
          const [selectedOperationObjectID, selectedOrderObjectID, depth] =
            queue[0];
          queue.shift();

          // if (depth > maxDepth) {
          //   continue;
          // }

          const orderRow = $RowsCached[selectedOrderObjectID];
          if (orderRow === undefined) {
            // Impossible branch?
            const correspondingRow =
              $RowsAllFilteredIncludingUnfoldedOperations.find(
                (item) => item.Row === selectedOrderObjectID
              );
            DownloadRowIfUnloaded(
              correspondingRow.Row,
              correspondingRow.Alias,
              correspondingRow.RowType
            );
          }

          let data = orderRow.Data;
          if ($GanttChartType == GanttStore.GanttChartTypes.PeggedOrderGantt) {
            data = orderRow.PeggedOrderData[currentStyleIndex];
          }

          const operationData = data.find(
            (r) => r._objectID == selectedOperationObjectID
          );

          if (!operationData) {
            // An order is clicked!
            return ret;
          }
          const operationDataIndex = data.findIndex(
            (r) => r._objectID == selectedOperationObjectID
          ); // This is the index of the operation (or pegged order) under this order

          const operationDataIndexInAll =
            $RowsAllFilteredIncludingUnfoldedOperations.findIndex(
              (r) => r.Alias == operationData._operation && r.IsOperation
            );
          if (operationDataIndexInAll == -1) {
            // Click an operation above the order bar when no operation unfolded
            return ret;
          }

          // The index of the operation in all displayed rows. If out of the display range, should also be correct relative index
          const operationDataIndexInDisplayedRows =
            operationDataIndexInAll - $RowsDisplayedStart;

          if (!operationData) {
            console.error("Operation (pegged order) data not found");
            return ret;
          }

          const split = (str) =>
            !str ? [] : str.split(";").filter((e) => e !== "");

          const prevOperationCodes = split(operationData._prevOpe);

          // console.log(prevOperationCodes);
          if (prevOperationCodes) {
            prevOperationCodes.forEach((operationCode) => {
              let objectID = data.find(
                (r) => r._operation == operationCode
              )?._objectID;

              if (
                $GanttChartType == GanttStore.GanttChartTypes.PeggedOrderGantt
              ) {
                // For pegged order gantt chart, this properties are objectID's of pegged orders
                objectID = Number(operationCode);
              }
              // object ID of the workInst of the previous operation
              if (!visited.has(objectID)) {
                visited.add(objectID);
                queue.push([objectID, selectedOrderObjectID, depth + 1]);
              }
              let prevOperationData = data.find((r) => r._objectID == objectID);
              let prevOperationDataIndex = data.findIndex(
                (r) => r._objectID == objectID
              );
              // console.log(prevOperationData, prevOperationDataIndex);
              if (prevOperationData) {
                let prevOperationIndexInDisplayedRows =
                  operationDataIndexInDisplayedRows +
                  prevOperationDataIndex -
                  operationDataIndex;
                ret.push([
                  CalculateStampX(
                    $ApplyResult
                      ? prevOperationData?._resultEnd ||
                          prevOperationData._productionArr?.[
                            prevOperationData._productionArr.length - 1
                          ]
                      : prevOperationData._productionArr?.[
                          prevOperationData._productionArr.length - 1
                        ]
                  ),
                  CalculateStampX(
                    $ApplyResult
                      ? operationData?._resultStart ||
                          operationData._productionArr?.[0]
                      : operationData._productionArr?.[0]
                  ),
                  prevOperationIndexInDisplayedRows,
                  operationDataIndexInDisplayedRows,
                ]);
              }
            });
          }

          const nextOperationCodes = split(operationData._nextOpe);
          if (nextOperationCodes) {
            nextOperationCodes.forEach((operationCode) => {
              let objectID = data.find(
                (r) => r._operation == operationCode
              )?._objectID;

              if (
                $GanttChartType == GanttStore.GanttChartTypes.PeggedOrderGantt
              ) {
                // For pegged order gantt chart, this properties are objectID's of pegged orders
                objectID = Number(operationCode);
              }

              // object ID of the workInst of the next operation
              if (!visited.has(objectID)) {
                visited.add(objectID);
                queue.push([objectID, selectedOrderObjectID, depth + 1]);
              }
              let nextOperationData = data.find((r) => r._objectID == objectID);
              let nextOperationDataIndex = data.findIndex(
                (r) => r._objectID == objectID
              );
              if (nextOperationData) {
                let nextOperationIndexInDisplayedRows =
                  operationDataIndexInDisplayedRows +
                  nextOperationDataIndex -
                  operationDataIndex;
                ret.push([
                  CalculateStampX(
                    $ApplyResult
                      ? operationData?._resultEnd ||
                          operationData._productionArr?.[
                            operationData._productionArr.length - 1
                          ]
                      : operationData._productionArr?.[
                          operationData._productionArr.length - 1
                        ]
                  ),
                  CalculateStampX(
                    $ApplyResult
                      ? nextOperationData?._resultStart ||
                          nextOperationData._productionArr?.[0]
                      : nextOperationData._productionArr?.[0]
                  ),
                  operationDataIndexInDisplayedRows,
                  nextOperationIndexInDisplayedRows,
                ]);
              }
            });
          }
          // console.log(selectedOperationObjectID, selectedOrderObjectID);
          // console.log(orderRow);
          // break;
        }
      }
      return ret;
    }
  );
</script>

<g>
  {#if $connections}
    {#each $connections as conn}
      {#if conn[2] >= 0}
        <line
          x1="{conn[0]}%"
          x2="{(conn[0] + conn[1]) / 2}%"
          y1="{($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) *
            conn[2] +
            ($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) * 0.5}%"
          y2="{($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) *
            conn[2] +
            ($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) * 0.5}%"
          style="stroke:{connectionLineColor};stroke-width:1"
          stroke-linecap="round"
        />
      {/if}
      <line
        x1="{(conn[0] + conn[1]) / 2}%"
        x2="{(conn[0] + conn[1]) / 2}%"
        y1="{($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) *
          conn[2] +
          ($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) * 0.5}%"
        y2="{($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) *
          conn[3] +
          ($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) * 0.5}%"
        style="stroke:{connectionLineColor};stroke-width:1"
        stroke-linecap="round"
      />
      {#if conn[3] < $DisplayedRowCount}
        <line
          x1="{(conn[0] + conn[1]) / 2}%"
          x2="{conn[1]}%"
          y1="{($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) *
            conn[3] +
            ($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) * 0.5}%"
          y2="{($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) *
            conn[3] +
            ($DisplayedRowCount > 0 ? 100 / $DisplayedRowCount : 100) * 0.5}%"
          style="stroke:{connectionLineColor};stroke-width:1"
          stroke-linecap="round"
        />
      {/if}
    {/each}
  {/if}
</g>

<style>
</style>
